import { useMemo, useState } from "react";
import { buildDailyAttendance } from "../lib/attendance.js";
import { eachDate, monthRange } from "../lib/date.js";
import "./HrmsV2Preview.css";

const PAYROLL_RULES = {
  dharmendra: { weeklyOffDay: "Friday", monthlySalary: 28300, bf: -2, incentive: "noida" },
  sumit: { weeklyOffDay: "Wednesday", monthlySalary: 18500, bf: 0, incentive: "ivision" },
  "gulveer singh": { weeklyOffDay: "Thursday", monthlySalary: 21500, bf: 5.5, incentive: "none" },
  virendra: { weeklyOffDay: "Wednesday", monthlySalary: 35000, bf: 25, incentive: "crossings" },
  aashif: { weeklyOffDay: "Wednesday", monthlySalary: 28000, bf: 0.5, incentive: "noida" }
};

const EXCLUDED_NAMES = new Set(["gaurav", "shishir", "ujjawal", "amandeep yadav"]);

const DEFAULT_COLLECTIONS = {
  noida: 0,
  ivision: 0,
  crossings: 0,
  "gaur city": 0
};

function thisMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function cleanName(value = "") {
  return String(value || "").trim().toLowerCase();
}

function employeeName(employee) {
  return employee?.fullName || employee?.name || employee?.email || "Employee";
}

function initials(name = "Employee") {
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "E";
}

function formatDate(dateText = "") {
  const date = new Date(`${dateText}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateText;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatShortDate(dateText = "") {
  const date = new Date(`${dateText}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateText;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function money(value = 0) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(value || 0));
}

function formatPunchTime(punch) {
  const raw = punch?.timestamp || punch?.time || punch?.createdAt || punch?.created_at || "";
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function getStoreName(stores, storeId) {
  const store = stores.find((item) => item.id === storeId);
  return store?.name || store?.code || storeId || "Store";
}

function storeKey(name = "") {
  const value = cleanName(name);
  if (value.includes("noida") || value.includes("sector") || value === "nd") return "noida";
  if (value.includes("ivision") || value.includes("mayur") || value === "iv") return "ivision";
  if (value.includes("cross") || value === "cr") return "crossings";
  if (value.includes("gaur") || value === "gc") return "gaur city";
  return value || "other";
}

function dayName(dateText) {
  return new Date(`${dateText}T00:00:00`).toLocaleDateString("en-US", { weekday: "long" });
}

function countWeekday(monthDates, weekday) {
  return monthDates.filter((date) => dayName(date) === weekday).length;
}

function ruleFor(employee, stores) {
  const key = cleanName(employeeName(employee));
  const stored = PAYROLL_RULES[key] || {};
  const storeName = getStoreName(stores, employee.storeId);
  return {
    weeklyOffDay: stored.weeklyOffDay || employee.weeklyOffDay || "",
    monthlySalary: Number(stored.monthlySalary || employee.monthlySalary || 0),
    bf: Number(stored.bf ?? employee.openingLeaveBalance ?? 0),
    incentive: stored.incentive || "none",
    storeName,
    storeKey: storeKey(storeName)
  };
}

function incentiveFor(rule, collection) {
  const amount = Number(collection || 0);
  if (rule === "noida") {
    if (amount >= 400000) return { rate: 2, amount: amount * 0.02 };
    if (amount >= 300000) return { rate: 1, amount: amount * 0.01 };
  }
  if (rule === "ivision") {
    if (amount >= 300000) return { rate: 1, amount: amount * 0.01 };
  }
  if (rule === "crossings") {
    if (amount >= 400000) return { rate: 1.5, amount: amount * 0.015 };
    if (amount >= 300000) return { rate: 1, amount: amount * 0.01 };
  }
  return { rate: 0, amount: 0 };
}

function strictSummary({ date, employee, punches, settings }) {
  const base = buildDailyAttendance({
    date,
    punches,
    weeklyOffDay: employee.weeklyOffDay,
    shift: {
      startTime: employee.shiftStart || settings?.defaultShiftStart,
      fullDayOutBy: employee.fullDayOutBy || settings?.fullDayOutBy,
      halfDayCutoff: employee.halfDayCutoff || settings?.halfDayCutoff,
      graceMinutes: employee.graceMinutes ?? settings?.graceMinutes
    }
  });

  const hasIn = Boolean(base.punchIn);
  const hasOut = Boolean(base.punchOut);

  if (hasIn && hasOut) {
    const isHalf = base.code === "HD";
    return {
      ...base,
      strictCode: isHalf ? "HD" : "P",
      strictLabel: isHalf ? "Half day" : "Present",
      issue: isHalf ? "Half day" : ""
    };
  }

  if (hasIn || hasOut) {
    return {
      ...base,
      strictCode: "M",
      strictLabel: "Missing punch",
      issue: hasIn ? "Punch-out missing" : "Punch-in missing"
    };
  }

  return {
    ...base,
    strictCode: "A",
    strictLabel: "Absent",
    issue: ""
  };
}

export default function HrmsV2Preview({ state, employees = [], stores = [] }) {
  const [month, setMonth] = useState(thisMonth());
  const [selectedDate, setSelectedDate] = useState(`${thisMonth()}-${String(new Date().getDate()).padStart(2, "0")}`);
  const [section, setSection] = useState("attendance");
  const [dayTab, setDayTab] = useState("exceptions");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [collections, setCollections] = useState(DEFAULT_COLLECTIONS);

  const monthDates = useMemo(() => {
    const range = monthRange(month);
    return eachDate(range.start, range.end);
  }, [month]);

  const firstWeekdayOffset = useMemo(() => {
    if (!monthDates.length) return 0;
    return (new Date(`${monthDates[0]}T00:00:00`).getDay() + 6) % 7;
  }, [monthDates]);

  const payrollEmployees = useMemo(() => {
    return (employees || [])
      .filter((employee) => employee && employee.active !== false)
      .filter((employee) => !EXCLUDED_NAMES.has(cleanName(employeeName(employee))));
  }, [employees]);

  const punchRows = state?.punches || [];

  const punchesFor = (employeeId, date) => (
    punchRows.filter((punch) => punch.employeeId === employeeId && punch.date === date)
  );

  const dayRows = useMemo(() => {
    return payrollEmployees.map((employee) => {
      const summary = strictSummary({
        date: selectedDate,
        employee,
        punches: punchesFor(employee.id, selectedDate),
        settings: state?.settings || {}
      });
      return {
        id: employee.id,
        employee,
        storeName: getStoreName(stores, employee.storeId),
        summary
      };
    });
  }, [payrollEmployees, punchRows, selectedDate, state?.settings, stores]);

  const dayCounts = useMemo(() => ({
    present: dayRows.filter((row) => row.summary.strictCode === "P").length,
    half: dayRows.filter((row) => row.summary.strictCode === "HD").length,
    absent: dayRows.filter((row) => row.summary.strictCode === "A").length,
    missing: dayRows.filter((row) => row.summary.strictCode === "M").length
  }), [dayRows]);

  const calendarCounts = useMemo(() => {
    return monthDates.reduce((map, date) => {
      const rows = payrollEmployees.map((employee) => strictSummary({
        date,
        employee,
        punches: punchesFor(employee.id, date),
        settings: state?.settings || {}
      }));
      map[date] = {
        p: rows.filter((row) => row.strictCode === "P").length,
        a: rows.filter((row) => row.strictCode === "A").length,
        e: rows.filter((row) => ["M", "HD"].includes(row.strictCode)).length
      };
      return map;
    }, {});
  }, [monthDates, payrollEmployees, punchRows, state?.settings]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = [...dayRows];
    if (dayTab === "exceptions") rows = rows.filter((row) => ["M", "HD"].includes(row.summary.strictCode));
    if (dayTab === "absent") rows = rows.filter((row) => row.summary.strictCode === "A");
    if (dayTab === "present") rows = rows.filter((row) => row.summary.strictCode === "P");
    if (q) rows = rows.filter((row) => `${employeeName(row.employee)} ${row.storeName} ${row.summary.strictLabel} ${row.summary.issue}`.toLowerCase().includes(q));
    const sort = { M: 1, HD: 2, A: 3, P: 4 };
    return rows.sort((a, b) => (sort[a.summary.strictCode] || 9) - (sort[b.summary.strictCode] || 9) || employeeName(a.employee).localeCompare(employeeName(b.employee)));
  }, [dayRows, dayTab, query]);

  const pageSize = 8;
  const visibleRows = filteredRows.slice(page * pageSize, page * pageSize + pageSize);

  const monthlyRows = useMemo(() => {
    return payrollEmployees.map((employee) => {
      const rule = ruleFor(employee, stores);
      const summaries = monthDates.map((date) => strictSummary({
        date,
        employee,
        punches: punchesFor(employee.id, date),
        settings: state?.settings || {}
      }));
      const present = summaries.filter((item) => item.strictCode === "P").length;
      const halfDays = summaries.filter((item) => item.strictCode === "HD").length;
      const absents = summaries.filter((item) => item.strictCode === "A").length;
      const missing = summaries.filter((item) => item.strictCode === "M").length;
      const fullOffs = absents + missing;
      const entitled = rule.weeklyOffDay ? countWeekday(monthDates, rule.weeklyOffDay) : 0;
      const halfUsed = Math.min(0.5, halfDays * 0.5);
      const halfLapsed = Math.max(0, 0.5 - halfUsed);
      const cf = rule.bf + entitled - fullOffs;
      const staffWelfare = present * 20;
      const incentive = incentiveFor(rule.incentive, collections[rule.storeKey]);
      return {
        employee,
        rule,
        present,
        halfDays,
        absents,
        missing,
        fullOffs,
        entitled,
        halfUsed,
        halfLapsed,
        cf,
        deductionDays: 0,
        staffWelfare,
        incentive,
        netDraft: rule.monthlySalary + staffWelfare
      };
    });
  }, [collections, monthDates, payrollEmployees, punchRows, state?.settings, stores]);

  const selectDate = (date) => {
    setSelectedDate(date);
    setPage(0);
  };

  const changeMonth = (value) => {
    setMonth(value);
    const todayMonth = thisMonth();
    const day = value === todayMonth ? String(new Date().getDate()).padStart(2, "0") : "01";
    setSelectedDate(`${value}-${day}`);
    setPage(0);
  };

  return (
    <section className="hrmsv2-shell">
      <div className="hrmsv2-header">
        <div>
          <span>HRMS V2 Preview</span>
          <h1>Attendance + Payroll Control</h1>
          <p>Read-only preview. Existing attendance marking and saved punch data are not changed.</p>
        </div>
        <div className="hrmsv2-actions">
          <input type="month" value={month} onChange={(event) => changeMonth(event.target.value)} />
          <button type="button">Draft only</button>
        </div>
      </div>

      <nav className="hrmsv2-tabs">
        {[
          ["attendance", "Attendance"],
          ["salary", "Salary Workspace"],
          ["collection", "Collection + Incentive"],
          ["exports", "Final Payroll"],
          ["rules", "Rules"]
        ].map(([key, label]) => (
          <button key={key} type="button" className={section === key ? "active" : ""} onClick={() => setSection(key)}>
            {label}
          </button>
        ))}
      </nav>

      {section === "attendance" ? (
        <>
          <div className="hrmsv2-kpis">
            <div><span>Date</span><strong>{formatShortDate(selectedDate)}</strong><small>Selected</small></div>
            <div><span>Present</span><strong>{dayCounts.present}</strong><small>In + out</small></div>
            <div><span>Absent</span><strong>{dayCounts.absent}</strong><small>No punch</small></div>
            <div><span>Issues</span><strong>{dayCounts.missing + dayCounts.half}</strong><small>Missing / half</small></div>
          </div>

          <div className="hrmsv2-attendance-grid">
            <div className="hrmsv2-card hrmsv2-calendar-box">
              <div className="hrmsv2-card-title">
                <h2>{new Date(`${month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</h2>
                <button type="button" onClick={() => selectDate(`${thisMonth()}-${String(new Date().getDate()).padStart(2, "0")}`)}>Today</button>
              </div>
              <div className="hrmsv2-week">
                {["M", "T", "W", "T", "F", "S", "S"].map((item, idx) => <b key={`${item}-${idx}`}>{item}</b>)}
              </div>
              <div className="hrmsv2-calendar">
                {Array.from({ length: firstWeekdayOffset }).map((_, index) => <i key={`blank-${index}`} />)}
                {monthDates.map((date) => {
                  const c = calendarCounts[date] || { p: 0, a: 0, e: 0 };
                  return (
                    <button key={date} type="button" className={selectedDate === date ? "active" : ""} onClick={() => selectDate(date)}>
                      <strong>{Number(date.slice(-2))}</strong>
                      <span>{c.p}</span>
                      <em>{c.a}</em>
                      {c.e ? <small>{c.e}</small> : null}
                    </button>
                  );
                })}
              </div>
              <div className="hrmsv2-calendar-legend">
                <span>Green = Present</span><span>Red = Absent</span><span>Yellow = Issues</span>
              </div>
            </div>

            <div className="hrmsv2-card hrmsv2-detail-box">
              <div className="hrmsv2-card-title">
                <div>
                  <h2>{formatDate(selectedDate)}</h2>
                  <p>Exceptions first. Present list is searchable and paginated.</p>
                </div>
              </div>

              <div className="hrmsv2-store-grid">
                {["noida", "ivision", "crossings", "gaur city"].map((key) => {
                  const rows = dayRows.filter((row) => storeKey(row.storeName) === key);
                  const label = key === "gaur city" ? "Gaur City" : key.charAt(0).toUpperCase() + key.slice(1);
                  return (
                    <div key={key}>
                      <b>{label}</b>
                      <span>{rows.filter((row) => row.summary.strictCode === "P").length} P · {rows.filter((row) => row.summary.strictCode !== "P").length} not present</span>
                    </div>
                  );
                })}
              </div>

              <div className="hrmsv2-segments">
                {["exceptions", "absent", "present", "all"].map((key) => (
                  <button key={key} type="button" className={dayTab === key ? "active" : ""} onClick={() => { setDayTab(key); setPage(0); }}>
                    {key === "all" ? "All" : key[0].toUpperCase() + key.slice(1)}
                  </button>
                ))}
              </div>

              <input className="hrmsv2-search" placeholder="Search employee, store, status" value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); }} />

              <div className="hrmsv2-row-list">
                {visibleRows.map((row) => (
                  <article key={row.id}>
                    <div className="hrmsv2-avatar">{initials(employeeName(row.employee))}</div>
                    <div>
                      <strong>{employeeName(row.employee)}</strong>
                      <small>{row.storeName} · {row.summary.strictLabel}{row.summary.issue ? ` · ${row.summary.issue}` : ""}</small>
                    </div>
                    <div className="hrmsv2-time">
                      <b>{formatPunchTime(row.summary.punchIn)} → {formatPunchTime(row.summary.punchOut)}</b>
                      <span>{row.summary.strictCode === "P" ? "Valid present" : "Review"}</span>
                    </div>
                  </article>
                ))}
                {!visibleRows.length ? <p className="hrmsv2-empty">No rows found.</p> : null}
              </div>

              <div className="hrmsv2-pager">
                <span>{filteredRows.length ? `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, filteredRows.length)} of ${filteredRows.length}` : "0 rows"}</span>
                <div>
                  <button type="button" disabled={page === 0} onClick={() => setPage((x) => Math.max(0, x - 1))}>Prev</button>
                  <button type="button" disabled={(page + 1) * pageSize >= filteredRows.length} onClick={() => setPage((x) => x + 1)}>Next</button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {section === "salary" ? (
        <div className="hrmsv2-card">
          <div className="hrmsv2-card-title">
            <div>
              <h2>Salary Workspace</h2>
              <p>Attendance-derived draft. Final deduction days remain editable by admin.</p>
            </div>
          </div>
          <div className="hrmsv2-table-scroll">
            <table className="hrmsv2-table">
              <thead>
                <tr>
                  <th>Employee</th><th>WO</th><th>BF</th><th>Full Offs</th><th>Half Day</th><th>CF</th><th>Ded Days</th><th>SW</th><th>Net Draft</th>
                </tr>
              </thead>
              <tbody>
                {monthlyRows.map((row) => (
                  <tr key={row.employee.id}>
                    <td><b>{employeeName(row.employee)}</b><small>{row.rule.storeName}</small></td>
                    <td>{row.entitled}</td>
                    <td>{row.rule.bf}</td>
                    <td>{row.fullOffs}</td>
                    <td>Used {row.halfUsed} / Lapsed {row.halfLapsed}</td>
                    <td className={row.cf < 0 ? "danger" : ""}>{row.cf}</td>
                    <td>{row.deductionDays}<small>Editable</small></td>
                    <td>{money(row.staffWelfare)}</td>
                    <td><b>{money(row.netDraft)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {section === "collection" ? (
        <div className="hrmsv2-two">
          <div className="hrmsv2-card">
            <div className="hrmsv2-card-title"><h2>Collection Entry</h2></div>
            <div className="hrmsv2-form">
              {Object.keys(collections).map((key) => (
                <label key={key}>
                  <span>{key === "gaur city" ? "Gaur City" : key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  <input value={collections[key]} onChange={(event) => setCollections((x) => ({ ...x, [key]: Number(event.target.value || 0) }))} />
                </label>
              ))}
            </div>
          </div>
          <div className="hrmsv2-card">
            <div className="hrmsv2-card-title"><h2>Incentive Record</h2><p>Recorded only unless marked payable.</p></div>
            <div className="hrmsv2-table-scroll">
              <table className="hrmsv2-table compact">
                <thead><tr><th>Employee</th><th>Rule</th><th>Rate</th><th>Recorded</th></tr></thead>
                <tbody>
                  {monthlyRows.map((row) => (
                    <tr key={row.employee.id}><td>{employeeName(row.employee)}</td><td>{row.rule.incentive}</td><td>{row.incentive.rate}%</td><td>{money(row.incentive.amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {section === "exports" ? (
        <div className="hrmsv2-kpis">
          <div><span>Staff Sheet</span><strong>Clean</strong><small>No audit notes</small></div>
          <div><span>Admin Sheet</span><strong>Detailed</strong><small>Corrections + flags</small></div>
          <div><span>Salary Slips</span><strong>Private</strong><small>One employee only</small></div>
          <div><span>Status</span><strong>Draft</strong><small>Approval required</small></div>
        </div>
      ) : null}

      {section === "rules" ? (
        <div className="hrmsv2-card">
          <div className="hrmsv2-card-title"><h2>Rules Applied</h2></div>
          <ul className="hrmsv2-rules">
            <li>Present only if punch-in and punch-out both exist.</li>
            <li>Only one punch is missing punch and counts absent until admin approval.</li>
            <li>Weekly off is monthly entitlement only. Employee can take off on any day.</li>
            <li>Monthly half day is 0.5 and lapses if unused. It does not carry forward.</li>
            <li>Aashif weekly off is Wednesday. Amandeep is excluded from active payroll.</li>
            <li>Gaur City/Aashif incentive uses Noida slabs. iVision incentive applies to Sumit, not Gulveer.</li>
          </ul>
        </div>
      ) : null}
    </section>
  );
}
